import { useTranslation } from "react-i18next"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useRequireAuth } from "@/hooks/useRequireAuth"
import { useQuery } from "@tanstack/react-query"
import { accountQueries } from "@/services/accountApi"
import { useLang } from "@/hooks/useLang"
import { LayoutDashboard, Key, CreditCard, Settings, Users } from "lucide-react"
import { Link, Outlet, useLocation } from "@tanstack/react-router"
import { cn } from "@/lib/utils"

const VALID_TABS = ['overview', 'billing', 'referrals', 'api', 'settings'] as const
type TabValue = typeof VALID_TABS[number]

interface AccountLayoutProps {
  children?: React.ReactNode
}

export function AccountLayout({ children }: AccountLayoutProps) {
  const { isAuthenticated } = useRequireAuth()
  const { t } = useTranslation(['app', 'common'])
  const location = useLocation()
  const lang = useLang()

  // Extract current tab from URL path
  const pathParts = location.pathname.split('/').filter(Boolean)
  const lastPart = pathParts[pathParts.length - 1]
  // Check if we're at the account index route (/$lang/account)
  const isOverviewRoute = lastPart === 'account' || (pathParts.length >= 2 && pathParts[pathParts.length - 2] === 'account' && !VALID_TABS.includes(lastPart as TabValue))
  const currentTab = isOverviewRoute ? 'overview' : lastPart as TabValue
  const activeTab = VALID_TABS.includes(currentTab) ? currentTab : 'overview'

  const {
    data: accountData,
    isLoading: isLoadingAccount,
  } = useQuery(accountQueries.me())

  const account = accountData?.account
  const isVerified = account?.verified ?? true

  if (!isAuthenticated) return null

  const TabLink = ({ value, icon: Icon, label }: { value: TabValue; icon: React.ElementType; label: string }) => {
    const isOverview = value === 'overview'
    return (
      <TabsTrigger
        value={value}
        className="gap-1.5 shrink-0"
        asChild
      >
        <Link
          to={isOverview ? "/$lang/account" : "/$lang/account/$tab"}
          params={{ lang, tab: value }}
          className={cn(
            "flex items-center gap-1.5",
            activeTab === value && "data-[state=active]:bg-background data-[state=active]:text-foreground"
          )}
        >
          <Icon className="h-4 w-4" />
          <span>{label}</span>
        </Link>
      </TabsTrigger>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {!isLoadingAccount && !isVerified && (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
            <p className="text-sm font-medium">{t("account.verificationWarning")}</p>
          </div>
        )}

        <div className="mb-8 space-y-2">
          <h1 className="text-3xl font-bold text-foreground">{t("account.title")}</h1>
          <p className="text-muted-foreground">{t("account.manage")}</p>
        </div>

        <Tabs value={activeTab}>
          <div className="-mx-4 px-4 overflow-x-auto scrollbar-none sm:mx-0 sm:px-0 sm:overflow-visible mb-6">
            <TabsList variant="line" className="w-full sm:w-fit">
              <TabLink
                value="overview"
                icon={LayoutDashboard}
                label={t("account.tabs.overview")}
              />
              <TabLink
                value="billing"
                icon={CreditCard}
                label={t("account.tabs.billing")}
              />
              <TabLink
                value="referrals"
                icon={Users}
                label="Referrals"
              />
              <TabLink
                value="api"
                icon={Key}
                label={t("account.tabs.agentApi")}
              />
              <TabLink
                value="settings"
                icon={Settings}
                label={t("account.tabs.settings")}
              />
            </TabsList>
          </div>

          {children || <Outlet />}
        </Tabs>
      </div>
    </div>
  )
}
